import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { simulateAPI } from '../lib/api';
import { Scale, Loader2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from '../hooks/useTranslation';

export default function SimulatePage() {
    const [domain, setDomain] = useState('');
    const [situation, setSituation] = useState('');
    const [restriction, setRestriction] = useState('');
    const [evaluation, setEvaluation] = useState(null);
    const [loading, setLoading] = useState(false);
    const { t } = useTranslation();

    const domainsConfig = t('case.domainsConfig');
    const domains = Array.isArray(domainsConfig) ? domainsConfig : [];

    const handleAnalyze = async () => {
        if (!domain || !situation || !restriction) {
            toast.error('Please fill in all fields (Domain, Situation, Restriction)');
            return;
        }

        setLoading(true);
        try {
            const response = await simulateAPI.simulate({
                domain,
                situation,
                restriction,
                language: localStorage.getItem('canon_lang') || 'en'
            });
            setEvaluation(response.data);
        } catch (error) {
            console.error('Simulation error:', error);
            toast.error('Failed to run simulation');
        } finally {
            setLoading(false);
        }
    };

    const ScoreBar = ({ label, score }) => (
        <div className="mb-4">
            <div className="flex justify-between mb-1">
                <span className="text-sm font-medium">{label}</span>
                <span className="text-sm text-muted-foreground">{score}/5</span>
            </div>
            <div className="w-full bg-muted rounded-full h-2.5">
                <div
                    className="bg-accent h-2.5 rounded-full transition-all duration-1000"
                    style={{ width: `${(score / 5) * 100}%` }}
                ></div>
            </div>
        </div>
    );

    return (
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="simulate-page">
            {/* Header */}
            <div className="mb-6">
                <div className="flex items-center space-x-2 text-muted-foreground text-sm mb-2">
                    <Scale className="h-4 w-4" />
                    <span>{t('simulate.simulateMode')}</span>
                </div>
                <h1 className="text-3xl font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>
                    {t('simulate.title')}
                </h1>
                <p className="text-muted-foreground mt-1">
                    {t('simulate.subtitle')}
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Input Section */}
                <div className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-lg">{t('simulate.buildCase.title')}</CardTitle>
                            <CardDescription>{t('simulate.buildCase.description')}</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Domain Selection */}
                            <div className="space-y-2">
                                <Label>{t('simulate.buildCase.domain')}</Label>
                                <Select value={domain} onValueChange={setDomain}>
                                    <SelectTrigger>
                                        <SelectValue placeholder={t('simulate.buildCase.selectDomain')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {domains.map((d) => (
                                            <SelectItem key={d.value} value={d.value}>
                                                <div>
                                                    <div className="font-medium">{d.label}</div>
                                                    <div className="text-xs text-muted-foreground">{d.description}</div>
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Situation */}
                            <div className="space-y-2">
                                <Label>{t('simulate.buildCase.situation')}</Label>
                                <Textarea
                                    value={situation}
                                    onChange={(e) => setSituation(e.target.value)}
                                    placeholder={t('simulate.buildCase.situationPlaceholder')}
                                    className="min-h-[100px]"
                                />
                            </div>

                            {/* Proposed Restriction */}
                            <div className="space-y-2">
                                <Label>{t('simulate.buildCase.restriction')}</Label>
                                <Textarea
                                    value={restriction}
                                    onChange={(e) => setRestriction(e.target.value)}
                                    placeholder={t('simulate.buildCase.restrictionPlaceholder')}
                                    className="min-h-[100px]"
                                />
                            </div>

                            {/* Action Button */}
                            <Button
                                onClick={handleAnalyze}
                                disabled={loading || !domain || !situation || !restriction}
                                className="w-full"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        {t('simulate.actions.evaluating')}
                                    </>
                                ) : (
                                    <>
                                        <Scale className="mr-2 h-4 w-4" />
                                        {t('simulate.actions.evaluate')}
                                    </>
                                )}
                            </Button>
                        </CardContent>
                    </Card>

                    <Card className="border-accent/50 bg-accent/5">
                        <CardContent className="pt-4 flex items-start space-x-3">
                            <FileText className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="font-medium">{t('simulate.notLegalAdvice.title')}</p>
                                <p className="text-sm text-muted-foreground mt-1">
                                    {t('simulate.notLegalAdvice.description')}
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Results Section */}
                <div>
                    <Card className="h-full">
                        <CardHeader>
                            <CardTitle className="text-lg flex items-center justify-between">
                                <span>{t('simulate.results.title')}</span>
                                {evaluation?.verdict && (
                                    <Badge
                                        variant={
                                            (evaluation.verdict === 'VALID' || evaluation.verdict === 'ΕΓΚΥΡΟ') ? 'default' :
                                                ((evaluation.verdict === 'INVALID' || evaluation.verdict === 'ΑΚΥΡΟ') ? 'destructive' : 'secondary')
                                        }
                                        className="ml-2 text-sm"
                                    >
                                        {evaluation.verdict}
                                    </Badge>
                                )}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {!evaluation ? (
                                <div className="h-[400px] flex flex-col items-center justify-center text-muted-foreground border-2 border-dashed rounded-lg p-8 text-center bg-muted/20">
                                    <Scale className="h-12 w-12 mb-4 opacity-50" />
                                    <p>{t('simulate.buildCase.description')}</p>
                                </div>
                            ) : (
                                <ScrollArea className="h-[500px] pr-4">
                                    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                                        {/* Scores */}
                                        <div className="space-y-4">
                                            <h4 className="font-medium flex items-center border-b pb-2">
                                                {t('simulate.results.scores')}
                                            </h4>
                                            <div className="pl-2">
                                                <ScoreBar label={t('simulate.results.necessity')} score={evaluation.evaluation.necessity} />
                                                <ScoreBar label={t('simulate.results.proportionality')} score={evaluation.evaluation.proportionality} />
                                                <ScoreBar label={t('simulate.results.libertyImpact')} score={evaluation.evaluation.liberty_impact} />
                                                <ScoreBar label={t('simulate.results.reversibility')} score={evaluation.evaluation.reversibility} />
                                            </div>
                                        </div>

                                        {/* Reasoning */}
                                        <div className="space-y-4">
                                            <h4 className="font-medium border-b pb-2">
                                                {t('simulate.results.reasoning')}
                                            </h4>
                                            <p className="text-sm leading-relaxed text-muted-foreground p-3 bg-muted/30 rounded border-l-2 border-accent">
                                                {evaluation.reasoning}
                                            </p>
                                        </div>

                                        {/* Citations */}
                                        <div className="space-y-4 mb-4">
                                            <h4 className="font-medium border-b pb-2">
                                                {t('simulate.results.citations')}
                                            </h4>
                                            <div className="flex flex-wrap gap-2">
                                                {evaluation.citations.map((cite, i) => (
                                                    <Badge key={i} variant="secondary">
                                                        Art. {cite}
                                                    </Badge>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </ScrollArea>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
